import { useMemo, useState } from "react";
import {
  type CombinedIncomeVerificationSchema,
  type Verification
} from "shared/types";
import { CombinedIncomeSubStep } from "shared/app/InviteAndCreateModal/slices/ManualApplication/constants";
import { combinedIncomeScreenFlows } from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/constants";
import {
  CombinedIncomeStepData,
  NavigationData
} from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/types";
import {
  getNewIncomeFlowStepCount,
  resolveFlowTarget,
  shouldSkipSupervisorDetails
} from "shared/app/InviteAndCreateModal/slices/ManualApplication/slices/CombinedIncome/utils";

export const useAddIncomeFlow = ({
  verification,
  initialStepData,
  navigateToReview
}: {
  verification?: Verification;
  initialStepData?: CombinedIncomeStepData;
  navigateToReview: (draft: CombinedIncomeStepData) => void;
}) => {
  const [stepData, setStepData] = useState<CombinedIncomeStepData>(
    initialStepData ?? {}
  );

  const patch = (data: Partial<CombinedIncomeStepData>) => {
    const { lastStep, ...rest } = data;

    if (lastStep === CombinedIncomeSubStep.AddAnotherIncome)
      navigateToReview({ ...stepData, ...rest });
    else setStepData((prev) => ({ ...prev, ...data }));
  };

  const subStep = stepData.lastStep ?? CombinedIncomeSubStep.SelectIncomeType;
  const stepDisplayCount = getNewIncomeFlowStepCount(subStep);

  const navigationData: NavigationData = useMemo(() => {
    const schema = verification?.settings as
      | CombinedIncomeVerificationSchema
      | undefined;

    return {
      hasIncome: true,
      incomeType: stepData.currentIncomeType,
      skipSupervisorDetails: shouldSkipSupervisorDetails(
        schema,
        stepData.currentIncomeType
      ),
      skipHousingAssistance: true
    };
  }, [verification?.settings, stepData.currentIncomeType]);

  const backStep = useMemo(
    () =>
      resolveFlowTarget(
        combinedIncomeScreenFlows[subStep].back,
        navigationData
      ),
    [subStep, navigationData]
  );

  const isEntrySubStep = !stepData.lastStep;
  const canGoBack =
    !isEntrySubStep &&
    Boolean(backStep) &&
    backStep !== CombinedIncomeSubStep.AddAnotherIncome;

  const back = () => {
    if (!canGoBack || !backStep) return;

    patch({ lastStep: backStep });
  };

  return { stepData, patch, subStep, stepDisplayCount, back, canGoBack };
};
